import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from '../slices/auth';

const TIME_PATTERN_EXAMPLES = [
  { pattern: '{{RelativeMonth|0}}',         example: 'Mar',     desc: 'current month' },
  { pattern: '{{RelativeMonth|-1}}',        example: 'Feb',     desc: 'last month' },
  { pattern: '{{RelativeMonth|-2}}',        example: 'Jan',     desc: '2 months ago' },
  { pattern: '{{RelativeYear|0}}',          example: 'FY2026',  desc: 'current fiscal year' },
  { pattern: '{{RelativeYear|-1}}',         example: 'FY2025',  desc: 'last fiscal year' },
  { pattern: '{{RelativeMonth|-1|%Y-%m}}',  example: '2025-02', desc: 'YYYY-MM format' },
  { pattern: '{{RelativeMonth|-1|%m}}',     example: '02',      desc: 'month number' },
  { pattern: '{{RelativeYear|-1|%Y}}',      example: '2025',    desc: '4-digit year' },
];

function PatternReference() {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={() => setOpen((v) => !v)}
        sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#64748b', p: 0, '&:hover': { background: 'none', color: '#3b82f6' } }}
      >
        {open ? 'Hide' : 'Show'} available time patterns
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1, p: 1.5, borderRadius: '6px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          {TIME_PATTERN_EXAMPLES.map(({ pattern, example, desc }) => (
            <Box key={pattern} sx={{ display: 'flex', gap: 1.5, mb: 0.5, alignItems: 'baseline' }}>
              <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#0369a1', whiteSpace: 'nowrap' }}>
                {pattern}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>→</Typography>
              <Typography variant="caption" sx={{ color: '#475569' }}>
                <strong>{example}</strong> ({desc})
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ElementPropertyModal({
  open,
  onClose,
  slideDataSource = '',
  slideIntent = '',
  slideQuery = '',
  onSave,
}) {
  const dispatch = useDispatch();
  const dbs = useSelector((state) => state.auth.user?.db);

  const [mode, setMode] = React.useState('all');
  const [dataSource, setDataSource] = React.useState(slideDataSource);
  const [queryMode, setQueryMode] = React.useState('prompt');
  const [intent, setIntent] = React.useState(slideIntent);
  const [query, setQuery] = React.useState(slideQuery);

  React.useEffect(() => {
    if (open && !dbs) dispatch(getUser());
  }, [open, dbs, dispatch]);

  React.useEffect(() => {
    if (open) {
      setMode(slideDataSource || slideIntent || slideQuery ? 'all' : 'all');
      setDataSource(slideDataSource);
      setIntent(slideIntent);
      setQuery(slideQuery);
      setQueryMode(slideQuery ? 'sql' : 'prompt');
    }
  }, [open, slideDataSource, slideIntent, slideQuery]);

  const handleSave = () => {
    if (typeof onSave === 'function') {
      onSave({
        data_source: mode === 'all' ? dataSource : '',
        intent: mode === 'all' && queryMode === 'prompt' ? intent : '',
        query: mode === 'all' && queryMode === 'sql' ? query : '',
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LayersIcon sx={{ fontSize: 20, color: '#64748b' }} />
        Slide Properties
      </DialogTitle>
      <DialogContent>

        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)} sx={{ mb: 2, mt: 0.5 }}>
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Use one data source &amp; query for all elements</Typography>}
          />
          <FormControlLabel
            value="per-element"
            control={<Radio size="small" />}
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Configure each element separately</Typography>}
          />
        </RadioGroup>

        {mode === 'all' && (
          <>
            <FormControl margin="dense" size="small" fullWidth variant="outlined">
              <InputLabel id="slide-ds-label">Data Source</InputLabel>
              <Select
                labelId="slide-ds-label"
                value={dataSource}
                label="Data Source"
                onChange={(e) => setDataSource(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {dbs && dbs.map((item) => (
                  <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Query Input
              </Typography>
              <ToggleButtonGroup
                value={queryMode}
                exclusive
                size="small"
                onChange={(_, v) => v && setQueryMode(v)}
                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
              >
                <ToggleButton value="prompt">Natural Language</ToggleButton>
                <ToggleButton value="sql">SQL</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {queryMode === 'prompt' && (
              <TextField
                margin="dense"
                label="Prompt / Intent"
                placeholder="e.g., Profitability summary for FY24 vs FY25"
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                helperText="Shared across all elements on this slide — LLM generates SQL automatically"
              />
            )}

            {queryMode === 'sql' && (
              <>
                <TextField
                  margin="dense"
                  label="SQL Query"
                  placeholder={"SELECT * FROM sales\nWHERE month = '{{RelativeMonth|-1}}'\n  AND year = '{{RelativeYear|0|%Y}}'"}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={5}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                  helperText="Use {{pattern}} for dynamic dates — see reference below"
                />
                <PatternReference />
              </>
            )}
          </>
        )}

        {mode === 'per-element' && (
          <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
              Select an element on the slide canvas, then use{' '}
              <strong>Object Properties</strong> to set the data source and query for that element individually.
            </Typography>
            {(slideDataSource || slideIntent || slideQuery) && (
              <Typography variant="caption" sx={{ color: '#f59e0b', display: 'block', mt: 1 }}>
                Saving will clear the current slide-level data source and query.
              </Typography>
            )}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}
